import { Card, Flex, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'

import { useTreatmentServiceGetConsultationDiagnosesEndpoint } from 'src/api/queries'
import {
  GetConsultationDiagnosesItem,
  GetConsultationDiagnosisResponse
} from 'src/api/requests'
import { Text } from 'src/components'
import { useLatestConsultationDiagnoses } from 'src/features/persons/hooks/useLatestConsultationDiagnoses'
import { useStyles } from 'src/features/persons/styles'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

type Diagnosis = {
  id?: string
  code?: string | null
  translation?: { ro?: string; ru?: string }
}

/** Patient tab: the current diagnosis, from the latest consultation that
 *  recorded one, and the diagnoses recorded at every consultation. A
 *  consultation records a single diagnosis; the preliminary diagnoses of older
 *  consultations are shown under it. */
export const PatientDiagnosesPage = () => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()
  const palette = useTheme()
  const { id: personId } = useParams()

  const pagination = useTablePagination()

  const { primaryDiagnostics, isLoading: isLatestLoading } =
    useLatestConsultationDiagnoses(personId)

  const { data, isLoading } =
    useTreatmentServiceGetConsultationDiagnosesEndpoint(
      {
        personId: String(personId),
        pageNumber: pagination.current,
        pageSize: pagination.pageSize
      },
      undefined,
      { enabled: !!personId }
    )

  const formatDiagnosis = (item: Diagnosis) => {
    const name = item.translation?.[currentLang.value]

    return item.code ? `${item.code} – ${name ?? ''}` : (name ?? '')
  }

  const renderDiagnoses = (items?: Diagnosis[] | null) =>
    items?.length ? (
      <Flex vertical gap={4}>
        {items.map((item) => (
          <Text key={item.id} weight={400}>
            {formatDiagnosis(item)}
          </Text>
        ))}
      </Flex>
    ) : (
      '-'
    )

  const renderTags = (items?: GetConsultationDiagnosisResponse[] | null) =>
    items?.length ? (
      <Flex wrap="wrap" gap={4}>
        {items.map((item) => (
          <Tag key={item.id}>{formatDiagnosis(item)}</Tag>
        ))}
      </Flex>
    ) : (
      '-'
    )

  const columns: ColumnsType<GetConsultationDiagnosesItem> = [
    {
      key: 'consultationDate',
      title: t('consultations.consultation_date'),
      render: (_, { consultationDate }) =>
        consultationDate
          ? dayjs(consultationDate).format(dayMonthYearFormatWithDot)
          : '-'
    },
    {
      key: 'consultationType',
      title: t('consultations.consultation_type'),
      render: (_, { consultationType }) =>
        consultationType ? t(`consultation_form.type.${consultationType}`) : '-'
    },
    {
      key: 'institution',
      title: t('common.institution'),
      render: (_, { institution }) => institution?.name ?? '-'
    },
    {
      key: 'diagnostics',
      title: t('consultations.final_diagnostic'),
      render: (_, { finalDiagnostics, preliminaryDiagnostics }) => (
        <Flex vertical gap={8}>
          {(!!finalDiagnostics?.length || !preliminaryDiagnostics?.length) &&
            renderDiagnoses(finalDiagnostics)}

          {!!preliminaryDiagnostics?.length && (
            <Flex vertical gap={4}>
              <Text size="sm" color={palette.colorTextDescription}>
                {t('persons.diagnoses_primary')}
              </Text>
              {renderDiagnoses(preliminaryDiagnostics)}
            </Flex>
          )}
        </Flex>
      )
    },
    {
      key: 'opportunisticDiseases',
      title: t('treatments.opportunistic_diseases'),
      render: (_, item) =>
        renderTags([
          ...(item.preliminaryOpportunisticDiseases ?? []),
          ...(item.finalOpportunisticDiseases ?? []).filter(
            (final) =>
              !item.preliminaryOpportunisticDiseases?.some(
                (preliminary) => preliminary.id === final.id
              )
          )
        ])
    }
  ]

  return (
    <Flex vertical gap={16}>
      <Text className={styles.text}>{t('persons.person_diagnoses')}</Text>

      <Card
        loading={isLatestLoading}
        title={t('consultations.final_diagnostic')}
      >
        {renderDiagnoses(primaryDiagnostics)}
      </Card>

      <Text className={styles.text}>{t('persons.diagnoses_history')}</Text>

      <Table
        rowKey="consultationId"
        loading={isLoading}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={{ ...pagination, total: data?.totalCount }}
        scroll={{ x: 'max-content' }}
      />
    </Flex>
  )
}
